import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { authService } from '@/services/authService'
import { Trash2, Smartphone, XCircle, RefreshCw } from 'lucide-react'
import { toast } from 'sonner'

export default function DeviceManagementPage() {
    const { t } = useTranslation()
    const [devices, setDevices] = useState<any[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

    const fetchDevices = async () => {
        try {
            setIsLoading(true)
            const data = await authService.getDevices()
            setDevices(data)
            setSelectedIds(new Set()) // Clear selection on refresh
        } catch (error) {
            toast.error(t('admin.devices.toast.loadError'))
        } finally {
            setIsLoading(false)
        }
    }

    const toggleSelection = (id: string) => {
        const newSelection = new Set(selectedIds)
        if (newSelection.has(id)) {
            newSelection.delete(id)
        } else {
            newSelection.add(id)
        }
        setSelectedIds(newSelection)
    }

    const toggleAll = () => {
        if (selectedIds.size === devices.filter(d => (d.isActive ?? d.active)).length) {
            setSelectedIds(new Set())
        } else {
            const activeIds = devices
                .filter(d => (d.isActive ?? d.active))
                .map(d => d.id)
            setSelectedIds(new Set(activeIds))
        }
    }

    const handleRevoke = async (id: string, name: string) => {
        if (!confirm(t('admin.devices.confirm.revoke', { name }))) return;

        try {
            await authService.revokeDevice(id)
            toast.success(t('admin.devices.toast.revokeSuccess', { name }))
            fetchDevices()
        } catch (error) {
            toast.error(t('admin.devices.toast.revokeError'))
        }
    }

    const handleBulkRevoke = async () => {
        const count = selectedIds.size
        if (!confirm(t('admin.devices.confirm.bulkRevoke', { count }))) return;

        try {
            setIsLoading(true)
            // Call revoke for each selected ID
            await Promise.all(Array.from(selectedIds).map(id => authService.revokeDevice(id)))
            toast.success(t('admin.devices.toast.bulkRevokeSuccess', { count }))
            fetchDevices()
        } catch (error) {
            toast.error(t('admin.devices.toast.bulkRevokeError'))
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        fetchDevices()
    }, [])

    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-black text-white tracking-tighter uppercase flex items-center gap-3">
                        <Smartphone className="w-8 h-8 text-blue-500" />
                        {t('admin.devices.title')}
                    </h1>
                    <p className="text-gray-400 text-sm font-medium mt-1">{t('admin.devices.subtitle')}</p>
                </div>
                <div className="flex gap-3">
                    {selectedIds.size > 0 && (
                        <button
                            onClick={handleBulkRevoke}
                            disabled={isLoading}
                            className="flex items-center gap-2 px-6 py-2 bg-red-600 hover:bg-red-500 text-white rounded-xl transition-all text-xs font-black uppercase tracking-widest shadow-lg shadow-red-600/20"
                        >
                            <XCircle className="w-4 h-4" />
                            {t('admin.devices.disconnectSelected', { count: selectedIds.size })}
                        </button>
                    )}
                    <button
                        onClick={fetchDevices}
                        disabled={isLoading}
                        className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 text-gray-300 rounded-xl transition-colors text-xs font-black uppercase tracking-widest disabled:opacity-50"
                    >
                        <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                        {t('admin.devices.refreshList')}
                    </button>
                </div>
            </div>

            <div className="bg-gray-900/40 backdrop-blur-xl border border-white/5 rounded-3xl overflow-hidden shadow-xl">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-white/[0.02] border-b border-white/5">
                                <th className="p-6 whitespace-nowrap w-10">
                                    <input
                                        type="checkbox"
                                        checked={devices.length > 0 && selectedIds.size === devices.filter(d => (d.isActive ?? d.active)).length}
                                        onChange={toggleAll}
                                        className="w-4 h-4 rounded border-white/10 bg-white/5 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-900"
                                    />
                                </th>
                                <th className="p-6 text-[10px] font-black text-gray-500 uppercase tracking-widest">{t('admin.devices.table.device')}</th>
                                <th className="p-6 text-[10px] font-black text-gray-500 uppercase tracking-widest">{t('admin.devices.table.user')}</th>
                                <th className="p-6 text-[10px] font-black text-gray-500 uppercase tracking-widest">{t('admin.devices.table.status')}</th>
                                <th className="p-6 text-[10px] font-black text-gray-500 uppercase tracking-widest">{t('admin.devices.table.linkedDate')}</th>
                                <th className="p-6 text-[10px] font-black text-gray-500 uppercase tracking-widest text-right">{t('admin.common.actions')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                            {isLoading && devices.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="p-12 text-center text-gray-500 italic text-sm">{t('admin.devices.loading')}</td>
                                </tr>
                            ) : devices.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="p-12 text-center text-gray-500 italic text-sm">{t('admin.devices.empty')}</td>
                                </tr>
                            ) : (
                                devices.map((device) => {
                                    const isActive = device.isActive !== undefined ? device.isActive : device.active;
                                    return (
                                        <tr key={device.id} className={`group hover:bg-white/[0.02] transition-colors ${selectedIds.has(device.id) ? 'bg-blue-600/5' : ''}`}>
                                            <td className="p-6">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedIds.has(device.id)}
                                                    onChange={() => toggleSelection(device.id)}
                                                    disabled={!isActive}
                                                    className="w-4 h-4 rounded border-white/10 bg-white/5 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-900 disabled:opacity-20"
                                                />
                                            </td>
                                            <td className="p-6">
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isActive ? 'bg-blue-500/10 text-blue-400' : 'bg-gray-800 text-gray-500'}`}>
                                                        <Smartphone className="w-5 h-5" />
                                                    </div>
                                                    <div>
                                                        <div className="font-bold text-white text-sm">{device.deviceName || t('admin.devices.unknown')}</div>
                                                        <div className="text-[10px] text-gray-500 font-mono uppercase truncate max-w-[120px]">{device.id}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="p-6">
                                                <div className="font-medium text-gray-300 text-sm">{device.username}</div>
                                            </td>
                                            <td className="p-6">
                                                {isActive ? (
                                                    <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/10 border border-green-500/20 text-green-400 text-[10px] font-black uppercase tracking-widest">
                                                        <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                                                        {t('admin.devices.statusActive')}
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/20 text-red-400 text-[10px] font-black uppercase tracking-widest opacity-60">
                                                        <XCircle className="w-3 h-3" />
                                                        {t('admin.devices.statusRevoked')}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="p-6 text-sm text-gray-400">
                                                {new Date(device.createdAt).toLocaleDateString()} <span className="text-xs opacity-50">{new Date(device.createdAt).toLocaleTimeString()}</span>
                                            </td>
                                            <td className="p-6 text-right">
                                                {isActive && (
                                                    <button
                                                        onClick={() => handleRevoke(device.id, device.deviceName)}
                                                        className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-500 hover:text-red-400 rounded-xl transition-all text-[10px] font-black uppercase tracking-widest border border-red-500/10 hover:border-red-500/30"
                                                        title={t('admin.devices.revokeTooltip')}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                        {t('admin.devices.disconnect')}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    )
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    )
}
